import React from "react";
import { api } from "@/lib/admin/api";
import { PageHead } from "@/pages/admin/Layout";
import { toast } from "@/components/admin/ui";

/* =========================================================================
   2High · Owner Cabinet — Draft Drops (mix-and-match cart submissions)
   ========================================================================= */

const STATUS_LABEL = {
  new: "Unviewed",
  viewed: "Viewed",
  confirmed: "Confirmed",
  shipped: "Shipped",
  closed: "Closed",
};

const STATUS_COLOR = {
  new:        "bg-[#c9a86b]/15 border-[#c9a86b]/45 text-[#c9a86b]",
  viewed:     "bg-[#0e1c2f]/60 border-[#5fa8e6]/35 text-[#7dd3fc]",
  confirmed:  "bg-[#152e1a]/60 border-[#5fa15a]/45 text-[#a8d39d]",
  shipped:    "bg-[#1a2e26]/60 border-[#7dd3a3]/45 text-[#bcf2cf]",
  closed:     "bg-[#1f2218]/60 border-[#2c3122] text-[#7a7c66]",
};

function money(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "$0";
  const v = Number(n);
  return v % 1 === 0 ? `$${v.toLocaleString()}` : `$${v.toFixed(2)}`;
}

function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!t) return "";
  const diff = Math.max(0, Date.now() - t);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function Drops() {
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState("all"); // all | new | viewed | confirmed | shipped | closed
  const [openId, setOpenId] = React.useState(null);

  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.listDraftOrders();
      setOrders(list);
    } catch (e) {
      toast("Failed to load drops", "error");
    } finally { setLoading(false); }
  }, []);
  React.useEffect(() => { reload(); }, [reload]);

  const newCount = orders.filter((o) => o.status === "new").length;

  const filtered = React.useMemo(() => {
    if (filter === "all") return orders;
    return orders.filter((o) => o.status === filter);
  }, [orders, filter]);

  async function markViewed(o) {
    if (o.status !== "new") return;
    try {
      const updated = await api.viewDraftOrder(o.id);
      setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, ...updated } : x)));
    } catch (e) { console.warn("[Drops] viewDraftOrder failed:", e); }
  }
  async function setStatus(o, status) {
    try {
      const updated = await api.updateDraftOrder(o.id, { status });
      setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, ...updated } : x)));
      toast(`Marked ${STATUS_LABEL[status] || status}`, "success");
    } catch (e) {
      toast("Failed to update", "error");
    }
  }
  async function remove(o) {
    if (!window.confirm("Delete this drop? This cannot be undone.")) return;
    try {
      await api.deleteDraftOrder(o.id);
      setOrders((prev) => prev.filter((x) => x.id !== o.id));
      if (openId === o.id) setOpenId(null);
      toast("Drop deleted", "success");
    } catch (e) { toast("Failed to delete", "error"); }
  }

  const totalValue = orders.reduce((s, o) => s + (Number(o.subtotal) || 0), 0);
  const newValue = orders.filter((o) => o.status === "new").reduce((s, o) => s + (Number(o.subtotal) || 0), 0);

  return (
    <div className="space-y-5">
      <PageHead
        title="Draft Drops"
        subtitle="Mix-and-match cart submissions from the public Drop Builder. Tap a row to view the breakdown and confirm by Telegram."
      />

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5" data-testid="drops-stats">
        <StatTile label="Total Drops" value={orders.length} />
        <StatTile label="Unviewed" value={newCount} accent />
        <StatTile label="New $ Value" value={money(newValue)} />
        <StatTile label="All-time $" value={money(totalValue)} />
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap items-center gap-1.5" data-testid="drops-filters">
        {[
          ["all", "All", orders.length],
          ["new", "Unviewed", newCount],
          ["viewed", "Viewed", orders.filter((o) => o.status === "viewed").length],
          ["confirmed", "Confirmed", orders.filter((o) => o.status === "confirmed").length],
          ["shipped", "Shipped", orders.filter((o) => o.status === "shipped").length],
          ["closed", "Closed", orders.filter((o) => o.status === "closed").length],
        ].map(([k, label, n]) => (
          <button
            key={k}
            type="button"
            onClick={() => setFilter(k)}
            data-testid={`drops-filter-${k}`}
            className={`px-3 py-1.5 rounded-full text-[10.5px] tracking-[.18em] uppercase font-mono border transition ${
              filter === k
                ? "border-[#c9a86b] bg-[#c9a86b]/10 text-[#c9a86b]"
                : "border-[#2c3122] text-[#7a7c66] hover:border-[#c9a86b]/40 hover:text-[#c9a86b]/80"
            }`}
          >
            {label} · {n}
          </button>
        ))}
        <div className="grow" />
        <button
          type="button"
          onClick={reload}
          data-testid="drops-refresh"
          className="px-3 py-1.5 rounded-full text-[10.5px] tracking-[.18em] uppercase font-mono border border-[#2c3122] text-[#7a7c66] hover:border-[#c9a86b]/50 hover:text-[#c9a86b] transition"
        >Refresh</button>
      </div>

      {loading ? (
        <div className="text-[12.5px] text-[#7a7c66] py-10 text-center font-mono">Loading drops…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[#2c3122] rounded-xl">
          <div className="text-[10.5px] tracking-[.24em] uppercase font-mono text-[#c9a86b] mb-2">— No drops yet</div>
          <p className="text-[13px] text-[#7a7c66] max-w-md mx-auto">
            When a member builds a private drop on the public site and confirms it, you'll see it land here.
          </p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="drops-list">
          {filtered.map((o) => (
            <li key={o.id} className={`rounded-xl border ${o.status === "new" ? "border-[#c9a86b]/35 bg-[#1a1810]/40" : "border-[#2c3122] bg-[#13160e]/40"}`} data-testid={`drop-${o.id}`}>
              <button
                type="button"
                onClick={() => { setOpenId(openId === o.id ? null : o.id); markViewed(o); }}
                className="w-full px-4 py-3 grid grid-cols-[auto_1fr_auto_auto] gap-3 items-center text-left"
              >
                <span className={`px-2 py-0.5 rounded-full text-[9.5px] tracking-[.2em] uppercase font-mono border ${STATUS_COLOR[o.status] || STATUS_COLOR.closed}`}>
                  {STATUS_LABEL[o.status] || o.status}
                </span>
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-[#c9c9a9] truncate">
                    {o.build_type || "Custom Mix"} · {(o.lines || []).length} {((o.lines || []).length) === 1 ? "item" : "items"}
                  </div>
                  <div className="text-[10.5px] tracking-[.16em] uppercase font-mono text-[#7a7c66] mt-1 flex flex-wrap gap-2">
                    <span>REF · {(o.id || "").slice(0, 8).toUpperCase()}</span>
                    {o.member_handle && <span>{o.member_handle}</span>}
                    <span>{relTime(o.received_at)}</span>
                  </div>
                </div>
                <div className="text-right font-mono text-[#c9a86b] text-[14px] font-semibold">
                  {money(o.subtotal)}
                </div>
                <span className="text-[#6e6f5f] text-[12px] font-mono">{openId === o.id ? "▲" : "▼"}</span>
              </button>

              {openId === o.id && (
                <div className="px-4 pb-4 pt-1 border-t border-[#2c3122]/60 space-y-3" data-testid={`drop-${o.id}-detail`}>
                  <ul className="text-[12.5px] divide-y divide-[#2c3122]/60">
                    {(o.lines || []).map((l, i) => (
                      <li key={`${l.product_id || l.product_name || 'line'}-${l.tier_key || i}`} className="grid grid-cols-[1fr_auto_auto_auto] gap-3 py-2 items-center">
                        <span className="text-[#c9c9a9]">{l.product_name}</span>
                        <span className="text-[10px] tracking-[.18em] uppercase font-mono text-[#c9a86b]/80">{l.tier_label || l.tier_key}</span>
                        <span className="text-[10px] tracking-[.18em] uppercase font-mono text-[#7a7c66]">× {l.qty}</span>
                        <span className="text-right font-mono text-[#c9a86b]">{money((l.unit_price || 0) * (l.qty || 1))}</span>
                      </li>
                    ))}
                  </ul>

                  {o.member_note && (
                    <div className="p-3 rounded-md bg-[#0a0c07] border border-[#2c3122] text-[12px] text-[#a9aa9a] italic">
                      "{o.member_note}"
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 pt-1">
                    {o.status !== "confirmed" && (
                      <button type="button" onClick={() => setStatus(o, "confirmed")}
                        className="px-3 py-1.5 rounded-full text-[10.5px] tracking-[.18em] uppercase font-mono border border-[#5fa15a]/50 bg-[#5fa15a]/10 text-[#a8d39d] hover:bg-[#5fa15a]/20 transition"
                        data-testid={`drop-${o.id}-confirm`}>
                        Mark Confirmed
                      </button>
                    )}
                    {o.status !== "shipped" && (
                      <button type="button" onClick={() => setStatus(o, "shipped")}
                        className="px-3 py-1.5 rounded-full text-[10.5px] tracking-[.18em] uppercase font-mono border border-[#7dd3a3]/50 bg-[#7dd3a3]/10 text-[#bcf2cf] hover:bg-[#7dd3a3]/20 transition"
                        data-testid={`drop-${o.id}-shipped`}>
                        Mark Shipped
                      </button>
                    )}
                    {o.status !== "closed" && (
                      <button type="button" onClick={() => setStatus(o, "closed")}
                        className="px-3 py-1.5 rounded-full text-[10.5px] tracking-[.18em] uppercase font-mono border border-[#2c3122] text-[#7a7c66] hover:border-[#c9a86b]/40 transition"
                        data-testid={`drop-${o.id}-close`}>
                        Close
                      </button>
                    )}
                    <button type="button" onClick={() => navigator.clipboard.writeText(JSON.stringify(o.lines, null, 2)).then(() => toast("Copied JSON", "success"))}
                      className="px-3 py-1.5 rounded-full text-[10.5px] tracking-[.18em] uppercase font-mono border border-[#2c3122] text-[#7a7c66] hover:border-[#c9a86b]/40 transition"
                      data-testid={`drop-${o.id}-copy`}>
                      Copy Lines
                    </button>
                    <button type="button" onClick={() => remove(o)}
                      className="ml-auto px-3 py-1.5 rounded-full text-[10.5px] tracking-[.18em] uppercase font-mono border border-[#7a4040]/40 text-[#ff8a8a]/80 hover:bg-[#7a4040]/15 transition"
                      data-testid={`drop-${o.id}-rm`}>
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatTile({ label, value, accent }) {
  return (
    <div className={`rounded-xl border p-3 ${accent ? "border-[#c9a86b]/40 bg-[#c9a86b]/8" : "border-[#2c3122] bg-[#13160e]/40"}`}>
      <div className="text-[9.5px] tracking-[.22em] uppercase font-mono text-[#7a7c66]">{label}</div>
      <div className={`mt-1.5 text-[20px] font-light tracking-tight font-mono ${accent ? "text-[#c9a86b]" : "text-[#c9c9a9]"}`}>{value}</div>
    </div>
  );
}
